
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CookieHeader from "@/components/legal/CookieHeader";
import CookieSection from "@/components/legal/CookieSection";
import CookieTypeCard from "@/components/legal/CookieTypeCard";
import BrowserInstructions from "@/components/legal/BrowserInstructions";
import ThirdPartyServices from "@/components/legal/ThirdPartyServices";
import ContactSection from "@/components/legal/ContactSection";

const Cookies = () => {
  const cookieTypes = [
    {
      title: "Essential Cookies",
      titleColor: "text-teedee-blue",
      bgColor: "bg-teedee-blue/10",
      description: "These cookies are necessary for our website to function properly. They enable basic features like page navigation, secure areas access, and form submissions. Our website cannot function properly without these cookies.",
      duration: "Session cookies (deleted when you close your browser) or up to 1 year"
    },
    {
      title: "Analytics Cookies",
      titleColor: "text-bobo-green",
      bgColor: "bg-bobo-green/10",
      description: "These cookies help us understand how visitors interact with our website by collecting information about pages visited, time spent on the site, and any error messages. This data is anonymized and helps us improve our educational content.",
      duration: "Up to 2 years"
    },
    {
      title: "Functional Cookies",
      titleColor: "text-brand-purple",
      bgColor: "bg-brand-purple/10",
      description: "These cookies remember your preferences and settings to provide a more personalized experience. For example, they might remember your language preference or the volume setting for our videos.",
      duration: "Up to 1 year"
    },
    {
      title: "Performance Cookies",
      titleColor: "text-golden-orange",
      bgColor: "bg-golden-orange/10",
      description: "These cookies collect information about how our website performs, including page load times and any technical issues. This helps us maintain and improve the speed and reliability of our site.",
      duration: "Up to 1 year"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
      <CookieHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-golden-orange">Cookie Policy for Bobo & Teedee®</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none space-y-8">
            <CookieSection title="1. What Are Cookies?" titleColor="text-golden-orange">
              <p className="text-gray-700 leading-relaxed">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Similar technologies include web beacons, pixels, and local storage, which serve similar purposes to cookies.
              </p>
            </CookieSection>

            <CookieSection title="2. How We Use Cookies" titleColor="text-teedee-blue">
              <p className="text-gray-700 leading-relaxed mb-4">
                Bobo & Teedee® uses cookies and similar technologies for several purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Essential Functions:</strong> To make our website work properly and securely</li>
                <li><strong>User Experience:</strong> To remember your preferences and settings</li>
                <li><strong>Analytics:</strong> To understand how visitors use our site and improve our content</li>
                <li><strong>Performance:</strong> To monitor and improve the speed and functionality of our site</li>
                <li><strong>Communication:</strong> To enable features like newsletter signups and contact forms</li>
              </ul>
            </CookieSection>

            <CookieSection title="3. Types of Cookies We Use" titleColor="text-bobo-green">
              <div className="space-y-6">
                {cookieTypes.map((cookie, index) => (
                  <CookieTypeCard
                    key={index}
                    title={cookie.title}
                    titleColor={cookie.titleColor}
                    bgColor={cookie.bgColor}
                    description={cookie.description}
                    duration={cookie.duration}
                  />
                ))}
              </div>
            </CookieSection>

            <CookieSection title="4. Third-Party Cookies" titleColor="text-brand-purple">
              <p className="text-gray-700 leading-relaxed mb-4">
                Some cookies on our site are set by third-party services we use to enhance your experience:
              </p>
              <ThirdPartyServices />
            </CookieSection>

            <CookieSection title="5. Children's Privacy and Cookies" titleColor="text-teedee-blue">
              <p className="text-gray-700 leading-relaxed">
                We are committed to protecting children's privacy online. Our use of cookies complies with COPPA (Children's Online Privacy Protection Act) requirements:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>We do not use cookies to collect personal information from children under 13</li>
                <li>Analytics cookies collect only anonymous, aggregated data</li>
                <li>Parents can control cookie settings on their child's device</li>
                <li>We regularly review our cookie practices to ensure child safety</li>
              </ul>
            </CookieSection>

            <CookieSection title="6. Managing Your Cookie Preferences" titleColor="text-bobo-green">
              <p className="text-gray-700 leading-relaxed mb-4">
                You have several options for managing cookies:
              </p>
              <BrowserInstructions />
            </CookieSection>

            <CookieSection title="7. Mobile Devices" titleColor="text-brand-purple">
              <p className="text-gray-700 leading-relaxed">
                If you access our website through a mobile browser or our mobile app, similar technologies may be used. You can manage these through your device settings:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>iOS:</strong> Settings {'>'} Privacy & Security {'>'} Tracking</li>
                <li><strong>Android:</strong> Settings {'>'} Privacy {'>'} Ads</li>
              </ul>
            </CookieSection>

            <CookieSection title="8. Updates to This Cookie Policy" titleColor="text-golden-orange">
              <p className="text-gray-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. When we make significant changes, we will notify users through our website or other appropriate means.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We encourage you to review this policy periodically to stay informed about how we use cookies.
              </p>
            </CookieSection>

            <CookieSection title="9. Contact Us" titleColor="text-teedee-blue">
              <ContactSection />
            </CookieSection>

            <div className="border-t pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                © 2025 Bobo & Teedee®. All rights reserved.<br />
                This Cookie Policy is effective as of January 1, 2025.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Cookies;
